import { ApiClient } from '@api/api-configuration';
import { BillingEntityTypeEnum, type CustomerProfileEntry } from '@api/READ_ONLY/billing_api/Api';
import type { CaregiverMinified, ExternalProvider } from '@api/READ_ONLY/caregiver_api/Api';
import type { Client } from '@api/READ_ONLY/client_api/Api';
import type { Doctor } from '@api/READ_ONLY/doctors_api/Api';
import { BILLING_QUERY_KEYS } from '@app/common/queryKeys';
import { useLastListParams } from '@app/features/exports/useLastListParams';
import type { GenericDataTableApiParams, GenericDataTableResponse } from '@konnecta/generic-table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export type EntityType = BillingEntityTypeEnum;

const CUSTOMER_PROFILE_QUERY_PARAM_KEYS = {
  ID: 'id',
  TYPE: 'type',
} as const;

const ENTITY_TYPE_VALUES = {
  CLIENT: BillingEntityTypeEnum.Client,
  DOCTOR: BillingEntityTypeEnum.Doctor,
  CAREGIVER: BillingEntityTypeEnum.Caregiver,
  CAREGIVER_CASH_REGISTER: BillingEntityTypeEnum.CaregiverCashRegister,
  PROVIDER: BillingEntityTypeEnum.Provider,
} as const;

const PROFILE_PROVIDER_QUERY_KEY = 'billing-customer-profile-provider';

const normalizeEntityTypeParam = (value: string | null): EntityType => {
  if (!value) {
    return ENTITY_TYPE_VALUES.CLIENT;
  }

  const normalized = value.trim().toLowerCase();

  if (
    normalized === ENTITY_TYPE_VALUES.CLIENT ||
    normalized === 'clients' ||
    normalized === 'customer' ||
    normalized === 'customers'
  ) {
    return ENTITY_TYPE_VALUES.CLIENT;
  }

  if (normalized === ENTITY_TYPE_VALUES.DOCTOR || normalized === 'doctors') {
    return ENTITY_TYPE_VALUES.DOCTOR;
  }

  if (
    normalized === ENTITY_TYPE_VALUES.CAREGIVER ||
    normalized === 'caregivers' ||
    normalized === 'nurse' ||
    normalized === 'nurses'
  ) {
    return ENTITY_TYPE_VALUES.CAREGIVER;
  }

  if (
    normalized === ENTITY_TYPE_VALUES.CAREGIVER_CASH_REGISTER ||
    normalized === 'cash' ||
    normalized === 'cash-register'
  ) {
    return ENTITY_TYPE_VALUES.CAREGIVER_CASH_REGISTER;
  }

  if (normalized === ENTITY_TYPE_VALUES.PROVIDER || normalized === 'providers') {
    return ENTITY_TYPE_VALUES.PROVIDER;
  }

  return ENTITY_TYPE_VALUES.CLIENT;
};

const parseEntityIdentifierParam = (value: string | null): number | undefined => {
  if (!value) {
    return undefined;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return undefined;
  }

  return parsed;
};

export const useCustomerProfile = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const queryClient = useQueryClient();

  const initialEntityType = normalizeEntityTypeParam(searchParams.get(CUSTOMER_PROFILE_QUERY_PARAM_KEYS.TYPE));
  const initialEntityIdentifier = parseEntityIdentifierParam(searchParams.get(CUSTOMER_PROFILE_QUERY_PARAM_KEYS.ID));

  const [entityType, setEntityType] = useState<EntityType>(initialEntityType);
  const [entityIdentifier, setEntityIdentifier] = useState<number | undefined>(initialEntityIdentifier);
  const [startingDate, setStartingDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endingDate, setEndingDate] = useState<Dayjs | null>(dayjs());
  // Bumped after an edit or delete so the ledger table refetches.
  const [ledgerVersion, setLedgerVersion] = useState(0);
  const { remember, getParams } = useLastListParams();
  const [editingPaymentId, setEditingPaymentId] = useState<number | null>(null);
  const [editingEntityIdentifier, setEditingEntityIdentifier] = useState<number | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(searchParamsKey);
    const nextEntityType = normalizeEntityTypeParam(params.get(CUSTOMER_PROFILE_QUERY_PARAM_KEYS.TYPE));
    const nextEntityIdentifier = parseEntityIdentifierParam(params.get(CUSTOMER_PROFILE_QUERY_PARAM_KEYS.ID));

    setEntityType(nextEntityType);
    setEntityIdentifier(nextEntityIdentifier);
  }, [searchParamsKey]);

  const selectedClientQuery = useQuery({
    queryKey: [BILLING_QUERY_KEYS.PAYMENT_FORM_CLIENT, entityIdentifier],
    enabled: entityType === ENTITY_TYPE_VALUES.CLIENT && Boolean(entityIdentifier),
    queryFn: async (): Promise<Client | null> => {
      const response = await ApiClient.clientApi.clients.getClientById(entityIdentifier as number);
      return response.data ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });

  const selectedDoctorQuery = useQuery({
    queryKey: [BILLING_QUERY_KEYS.PAYMENT_FORM_DOCTOR, entityIdentifier],
    enabled: entityType === ENTITY_TYPE_VALUES.DOCTOR && Boolean(entityIdentifier),
    queryFn: async (): Promise<Doctor | null> => {
      const response = await ApiClient.doctorsApi.doctors.getDoctorById(entityIdentifier as number);
      return response.data ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });

  // caregiver_cash_register accounts are keyed by the caregiver id, so the selected
  // entity for that type is fetched as a caregiver, reusing the caregiver-by-id query.
  const isCaregiverBackedEntity =
    entityType === ENTITY_TYPE_VALUES.CAREGIVER || entityType === ENTITY_TYPE_VALUES.CAREGIVER_CASH_REGISTER;

  const selectedCaregiverQuery = useQuery({
    queryKey: [BILLING_QUERY_KEYS.PAYMENT_FORM_CAREGIVER, entityIdentifier],
    enabled: isCaregiverBackedEntity && Boolean(entityIdentifier),
    queryFn: async (): Promise<CaregiverMinified | null> => {
      const response = await ApiClient.caregiverApi.caregivers.getCaregiverById(entityIdentifier as number);
      return (response.data as CaregiverMinified) ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });

  const selectedProviderQuery = useQuery({
    queryKey: [PROFILE_PROVIDER_QUERY_KEY, entityIdentifier],
    enabled: entityType === ENTITY_TYPE_VALUES.PROVIDER && Boolean(entityIdentifier),
    queryFn: async (): Promise<ExternalProvider | null> => {
      const response = await ApiClient.caregiverApi.externalProviders.getExternalProvider(entityIdentifier as number);
      return response.data ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });

  const canFetchCustomerProfile = typeof entityIdentifier === 'number' && entityIdentifier > 0;

  const startAt = startingDate ? startingDate.startOf('day').toISOString() : null;
  const endAt = endingDate ? endingDate.endOf('day').toISOString() : null;

  const fetchLedger = useCallback(
    async ({ limit, offset }: GenericDataTableApiParams): Promise<GenericDataTableResponse<CustomerProfileEntry>> => {
      if (!canFetchCustomerProfile) {
        return { data: [], total: 0 };
      }
      const response = await ApiClient.billingApi.billing.getCustomerProfile(
        remember({ entityType, entityId: entityIdentifier ?? null, startAt, endAt, offset, limit })
      );
      return { data: response.data.data ?? [], total: response.data.total };
    },
    [canFetchCustomerProfile, remember, entityType, entityIdentifier, startAt, endAt]
  );

  // The ledger shown; a new one remounts the table, which starts it on page 1.
  const ledgerKey = JSON.stringify([entityType, entityIdentifier ?? null, startAt, endAt]);
  // Same ledger re-read after an edit or delete, on the page the user is on.
  const ledgerRefreshToken = `${ledgerKey}:${ledgerVersion}`;

  const updateEntitySearchParams = (nextType: EntityType, nextIdentifier?: number) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    nextSearchParams.set(CUSTOMER_PROFILE_QUERY_PARAM_KEYS.TYPE, nextType);

    if (typeof nextIdentifier === 'number' && nextIdentifier > 0) {
      nextSearchParams.set(CUSTOMER_PROFILE_QUERY_PARAM_KEYS.ID, String(nextIdentifier));
    } else {
      nextSearchParams.delete(CUSTOMER_PROFILE_QUERY_PARAM_KEYS.ID);
    }

    setSearchParams(nextSearchParams, { replace: true });
  };

  const changeEntityType = (nextType: EntityType) => {
    updateEntitySearchParams(nextType);
  };

  const changeEntityIdentifier = (nextIdentifier: number | undefined) => {
    updateEntitySearchParams(entityType, nextIdentifier);
  };

  const changeStartingDate = (nextDate: Dayjs | null) => {
    setStartingDate(nextDate);
  };

  const changeEndingDate = (nextDate: Dayjs | null) => {
    setEndingDate(nextDate);
  };

  const openEditModal = useCallback((paymentId: number, entityIdentifier: number) => {
    setEditingPaymentId(paymentId);
    setEditingEntityIdentifier(entityIdentifier);
  }, []);

  const closeEditModal = () => {
    setEditingPaymentId(null);
    setEditingEntityIdentifier(null);
  };

  const reloadLedger = useCallback(() => setLedgerVersion((v) => v + 1), []);

  const deletePaymentMutation = useMutation({
    mutationFn: async (paymentId: number) => {
      await ApiClient.billingApi.billing.deleteTransaction(paymentId);
    },
    onSuccess: () => {
      setLedgerVersion((v) => v + 1);
      queryClient.invalidateQueries({ queryKey: [BILLING_QUERY_KEYS.RECENT_PAYMENTS_SIDEBAR] });
    },
  });

  return {
    entityType,
    entityIdentifier,
    startingDate,
    endingDate,
    fetchLedger,
    canFetchLedger: canFetchCustomerProfile,
    ledgerKey,
    ledgerRefreshToken,
    reloadLedger,
    getLedgerExportParams: getParams,
    editingPaymentId,
    editingEntityIdentifier,
    selectedClient:
      entityType === ENTITY_TYPE_VALUES.CLIENT && entityIdentifier
        ? (selectedClientQuery.data ?? undefined)
        : undefined,
    selectedDoctor:
      entityType === ENTITY_TYPE_VALUES.DOCTOR && entityIdentifier
        ? (selectedDoctorQuery.data ?? undefined)
        : undefined,
    selectedCaregiver:
      isCaregiverBackedEntity && entityIdentifier ? (selectedCaregiverQuery.data ?? undefined) : undefined,
    selectedProvider:
      entityType === ENTITY_TYPE_VALUES.PROVIDER && entityIdentifier
        ? (selectedProviderQuery.data ?? undefined)
        : undefined,
    changeEntityType,
    changeEntityIdentifier,
    changeStartingDate,
    changeEndingDate,
    openEditModal,
    closeEditModal,
    deletePaymentById: deletePaymentMutation.mutate,
    isDeletingPayment: deletePaymentMutation.isPending,
  };
};
